import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from 'antd';
import styled, { css, keyframes } from 'styled-components';
import { routes } from '../../config/config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const calPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50%       { transform: scale(1.15); opacity: 1; }
`;

const clockTick = keyframes`
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.18); }
`;

const timeBlink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.3; }
`;

const menuStatus = css`
  background: linear-gradient(90deg, rgba(24,144,255,0.18) 0%, rgba(24,144,255,0.05) 100%);
  color: #fff;
  border-left: 3px solid #1890ff;
`;

const Aside = styled.aside`
  top: 0;
  left: 0;
  display: block;
  position: fixed;
  min-height: 100%;
  transition: width 0.3s cubic-bezier(.22,1,.36,1), opacity 0.3s ease;
  z-index: 5;
  background: linear-gradient(180deg, #1a2530 0%, #1e2d38 100%);
  padding-top: 54px;
  overflow: hidden;
  box-shadow: 2px 0 16px rgba(0,0,0,0.18);

  ${props => props.viewmenu
    ? `width: 14em; opacity: 1;`
    : `width: 0px; opacity: 0;`
  }

  @media(max-width: 970px) {
    ${props => props.viewmenu
      ? `width: 0px; opacity: 0;`
      : `width: 14em; opacity: 1;`
    }
  }
`;

const DateViewer = styled.div`
  color: #a0b4bf;
  text-align: center;
  padding: 14px 12px;
  margin: 8px 12px;
  border-radius: 8px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.06);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
`;

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;

  .anticon-calendar {
    color: #1890ff;
    animation: ${calPulse} 2.5s ease-in-out infinite;
    font-size: 13px;
  }
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(24,144,255,0.08);
  border: 1px solid rgba(24,144,255,0.15);

  .anticon-clock-circle {
    color: #13c2c2;
    font-size: 12px;
    animation: ${clockTick} 1s ease-in-out infinite;
  }
`;

const TimeDisplay = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 1px;
  font-variant-numeric: tabular-nums;
`;

const TimeSep = styled.span`
  color: #1890ff;
  font-weight: 700;
  animation: ${timeBlink} 1s step-end infinite;
`;

const AmPm = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #13c2c2;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const PhFlag = styled.span`
  font-size: 13px;
  line-height: 1;
`;

const SectionDivider = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #4a6070;
  text-transform: uppercase;
  padding: 14px 18px 6px;
`;

const StyledUL = styled.ul`
  padding: 0 8px;
  margin: 0;
  list-style-type: none;
`;

const StyledList = styled.li`
  padding: 0;
  margin: 2px 0;
  animation: ${slideIn} 0.3s ease both;

  &:nth-child(1)  { animation-delay: 0.05s; }
  &:nth-child(2)  { animation-delay: 0.08s; }
  &:nth-child(3)  { animation-delay: 0.11s; }
  &:nth-child(4)  { animation-delay: 0.14s; }
  &:nth-child(5)  { animation-delay: 0.17s; }
  &:nth-child(6)  { animation-delay: 0.20s; }
  &:nth-child(7)  { animation-delay: 0.23s; }
  &:nth-child(8)  { animation-delay: 0.26s; }
`;

const StyledLink = styled(Link)`
  font-size: 13px;
  padding: 11px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
  border-bottom: none;
  text-decoration: none;
  text-transform: capitalize;
  transition: all 0.18s ease;
  position: relative;
  overflow: hidden;

  ${props => props.active ? menuStatus : 'color: #7f9aaa;'}

  &:hover {
    ${menuStatus}
    color: #fff;
    transform: translateX(2px);
  }

  .sidebar-icon {
    width: 18px;
    text-align: center;
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }

  &:hover .sidebar-icon,
  ${props => props.active && css`.sidebar-icon`} {
    color: #1890ff;
    transform: scale(1.12);
  }
`;

const useManilaClock = () => {
  const [time, setTime] = useState(() =>
    new Date().toLocaleString('en-US', { timeZone: 'Asia/Manila' })
  );

  useEffect(() => {
    const tick = () =>
      setTime(new Date().toLocaleString('en-US', { timeZone: 'Asia/Manila' }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const d = new Date(time);
  const hours12 = d.getHours() % 12 || 12;
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
  return { hours12, minutes, seconds, ampm };
};

const Sidebar = ({ viewMenu }) => {
  const pathname = useLocation().pathname;
  const { hours12, minutes, seconds, ampm } = useManilaClock();

  return (
    <Aside viewmenu={+viewMenu}>
      <DateViewer>
        <DateRow>
          <Icon type="calendar" />
          {moment().format('ddd, MMM D YYYY')}
        </DateRow>
        <TimeRow>
          <Icon type="clock-circle" />
          <TimeDisplay>
            {hours12}<TimeSep>:</TimeSep>{minutes}<TimeSep>:</TimeSep>{seconds}
          </TimeDisplay>
          <AmPm>{ampm}</AmPm>
          <PhFlag>🇵🇭</PhFlag>
        </TimeRow>
      </DateViewer>

      <SectionDivider>Navigation</SectionDivider>

      <StyledUL>
        {routes.map((data, i) => {
          if (!data.view) return false;
          const isActive = data.path === pathname;
          return (
            <StyledList key={i}>
              <StyledLink to={data.path} active={+isActive}>
                <span className="sidebar-icon">
                  <FontAwesomeIcon icon={data.icon} />
                </span>
                <span>{data.title}</span>
                {isActive && (
                  <span style={{
                    marginLeft: 'auto',
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: '#1890ff',
                    flexShrink: 0,
                    boxShadow: '0 0 6px #1890ff',
                  }} />
                )}
              </StyledLink>
            </StyledList>
          );
        })}
      </StyledUL>
    </Aside>
  );
};

export default Sidebar;